import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Anchor,
	ArrowLeftRight,
	CalendarRange,
	CheckSquare,
	ClipboardCopy,
	Loader2,
	Maximize2,
	Pencil,
	TrendingUp,
	X,
} from "lucide-react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
	ProgramSourcePicker,
	CURRENT_PROGRAM_SOURCE,
} from "./ProgramSourcePicker";
import {
	ReferenceWeekView,
	exerciseKey,
	isSharedReadonly,
} from "./ReferenceWeekView";
import { HintTooltip } from "./HintTooltip";
import { ProgressionMatrix } from "./ProgressionMatrix";
import {
	getNormalizedDayExercises,
	isWeekContentLoaded,
} from "../../lib/programBuilderUtils";
import { fetchAPI } from "../../lib/utils";
import { useLanguage } from "../../contexts/LanguageContext";
import { toast } from "sonner";

// Survives unmount (closing the dock) without surviving a reload, so reopening
// the panel restores the reference you were looking at while a fresh page load
// starts clean. Same trick as `lastMobileNavIndex` in Layout.js.
let lastReferenceState = null;
const referenceProgramCache = new Map();
const REFERENCE_CACHE_LIMIT = 8;

/**
 * Read-only reference on a week of any program, shown beside the editor.
 *
 * Two rules drive the whole component:
 *
 *  1. When the source IS the program being edited, the panel reads the live
 *     React state passed in `program` and NEVER refetches. Otherwise a coach
 *     comparing week 5 against the week 4 they just edited would be shown the
 *     last saved version — a silent lie that would kill trust in the feature.
 *  2. Nothing here writes. Exercises leave the panel only through the
 *     clipboard, and are re-cloned on paste by the editor.
 */
export function ReferencePanel({
	program,
	programId,
	selectedWeekIndex,
	isDesktopViewport,
	initialReference,
	onCopy,
	onNavigate,
	onSwap,
	onContextChange,
	onRequestWeeks,
}) {
	const { t } = useLanguage();
	const [sourceId, setSourceId] = useState(
		() => lastReferenceState?.sourceId || CURRENT_PROGRAM_SOURCE,
	);
	const [remoteProgram, setRemoteProgram] = useState(() =>
		lastReferenceState?.sourceId &&
		lastReferenceState.sourceId !== CURRENT_PROGRAM_SOURCE
			? referenceProgramCache.get(lastReferenceState.sourceId) || null
			: null,
	);
	const [loading, setLoading] = useState(false);
	const [weekIndex, setWeekIndex] = useState(() =>
		typeof lastReferenceState?.weekIndex === "number"
			? lastReferenceState.weekIndex
			: Math.max(0, (selectedWeekIndex || 0) - 1),
	);
	const [followEditor, setFollowEditor] = useState(
		() => lastReferenceState?.followEditor ?? true,
	);
	const [selectionMode, setSelectionMode] = useState(false);
	const [selectedKeys, setSelectedKeys] = useState(() => new Set());
	const [viewMode, setViewMode] = useState(
		() => lastReferenceState?.viewMode || "week",
	);
	// The matrix needs ~1000px for five weeks; the dock rarely has it.
	const [isMatrixFullscreen, setIsMatrixFullscreen] = useState(false);

	useEffect(() => {
		lastReferenceState = { sourceId, weekIndex, followEditor, viewMode };
	}, [sourceId, weekIndex, followEditor, viewMode]);

	const isCurrentSource = sourceId === CURRENT_PROGRAM_SOURCE;
	const sourceProgram = isCurrentSource ? program : remoteProgram;
	const weeks = useMemo(() => sourceProgram?.weeks || [], [sourceProgram]);

	const safeWeekIndex =
		weeks.length === 0 ? 0 : Math.min(weekIndex, weeks.length - 1);
	const week = weeks[safeWeekIndex];
	const isWeekMode = viewMode !== "progression";

	// Older weeks of the current program arrive as metadata-only skeletons (see
	// RECENT_WEEKS_WINDOW in ProgramBuilder). Ask the editor to fetch whichever
	// ones this panel is about to render: the single reference week, or every week
	// when the progression matrix compares them side by side.
	useEffect(() => {
		if (!isCurrentSource || !onRequestWeeks) return;
		const shown = isWeekMode ? [week] : weeks;
		const missing = shown
			.filter((candidate) => candidate && !isWeekContentLoaded(candidate))
			.map((candidate) => candidate.week_id)
			.filter(Boolean);
		if (missing.length > 0) {
			onRequestWeeks(missing);
		}
	}, [isCurrentSource, isWeekMode, week, weeks, onRequestWeeks]);

	// "Follow the editor" keeps the panel one week behind while writing, which is
	// the progression-review case the whole panel exists for.
	useEffect(() => {
		if (!followEditor || !isCurrentSource) return;
		setWeekIndex(Math.max(0, (selectedWeekIndex || 0) - 1));
	}, [followEditor, isCurrentSource, selectedWeekIndex]);

	useEffect(() => {
		setSelectedKeys(new Set());
	}, [sourceId, safeWeekIndex]);

	// A remote source must never be the program the editor has open: that would
	// render a fetched copy (last saved version) next to the live one. Reachable
	// after an editor/reference swap, which navigates the editor onto what was
	// until a moment ago the reference.
	useEffect(() => {
		if (!isCurrentSource && sourceId === programId) {
			setSourceId(CURRENT_PROGRAM_SOURCE);
			setRemoteProgram(null);
			setFollowEditor(true);
		}
	}, [isCurrentSource, sourceId, programId]);

	// The editor shows an inline delta badge against whatever week this panel is
	// pointing at, so it needs to know. One-way notification — the panel stays
	// the owner of its own state.
	useEffect(() => {
		if (!onContextChange) return;
		onContextChange({
			isCurrentSource,
			weekIndex: safeWeekIndex,
			viewMode,
		});
	}, [onContextChange, isCurrentSource, safeWeekIndex, viewMode]);

	useEffect(
		() => () => {
			// Closing the dock must clear the badges in the editor.
			if (onContextChange) onContextChange(null);
		},
		[onContextChange],
	);

	const handleSourceChange = useCallback(
		async (nextSourceId) => {
			setSourceId(nextSourceId);
			setFollowEditor(nextSourceId === CURRENT_PROGRAM_SOURCE);

			if (nextSourceId === CURRENT_PROGRAM_SOURCE) {
				setRemoteProgram(null);
				setWeekIndex(Math.max(0, (selectedWeekIndex || 0) - 1));
				return;
			}

			const cached = referenceProgramCache.get(nextSourceId);
			if (cached) {
				setRemoteProgram(cached);
				setWeekIndex(0);
				return;
			}

			setLoading(true);
			try {
				const detail = await fetchAPI(`/programs/${nextSourceId}`);
				if (referenceProgramCache.size >= REFERENCE_CACHE_LIMIT) {
					referenceProgramCache.delete(
						referenceProgramCache.keys().next().value,
					);
				}
				referenceProgramCache.set(nextSourceId, detail);
				setRemoteProgram(detail);
				setWeekIndex(0);
			} catch (error) {
				toast.error(error?.message || t("pbReferenceLoadError"));
				setSourceId(CURRENT_PROGRAM_SOURCE);
				setRemoteProgram(null);
			} finally {
				setLoading(false);
			}
		},
		[selectedWeekIndex, t],
	);

	// A swap hands over the program we came from; apply it once.
	const appliedInitialReferenceRef = useRef(null);
	useEffect(() => {
		const target = initialReference?.programId;
		if (!target || appliedInitialReferenceRef.current === target) return;
		appliedInitialReferenceRef.current = target;
		handleSourceChange(target).then(() => {
			if (typeof initialReference.weekIndex === "number") {
				setWeekIndex(initialReference.weekIndex);
			}
		});
	}, [initialReference, handleSourceChange]);

	const buildSource = useCallback(
		(dayIndex) => ({
			programTitle: (sourceProgram?.title || "").trim(),
			weekLabel: week?.label || `${t("weekShort")} ${safeWeekIndex + 1}`,
			dayTitle:
				dayIndex === null || dayIndex === undefined
					? ""
					: (week?.days?.[dayIndex]?.title || "").trim(),
		}),
		[sourceProgram, week, safeWeekIndex, t],
	);

	const handleCopyExercise = useCallback(
		(dayIndex, exerciseIndex) => {
			const exercises = getNormalizedDayExercises(week?.days?.[dayIndex]);
			const exercise = exercises[exerciseIndex];
			if (!exercise) return;
			onCopy({
				kind: "exercises",
				items: [{ ...exercise }],
				source: buildSource(dayIndex),
			});
		},
		[week, onCopy, buildSource],
	);

	const handleCopyDay = useCallback(
		(dayIndex) => {
			const exercises = getNormalizedDayExercises(
				week?.days?.[dayIndex],
			).filter((exercise) => !isSharedReadonly(exercise));
			if (exercises.length === 0) {
				toast.error(t("pbClipboardNothingToCopy"));
				return;
			}
			onCopy({
				kind: "day",
				items: exercises.map((exercise) => ({ ...exercise })),
				source: buildSource(dayIndex),
			});
		},
		[week, onCopy, buildSource, t],
	);

	const handleToggleSelect = useCallback((dayIndex, exerciseIndex) => {
		setSelectedKeys((prev) => {
			const next = new Set(prev);
			const key = exerciseKey(dayIndex, exerciseIndex);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	}, []);

	const handleCopySelection = useCallback(() => {
		const items = [];
		(week?.days || []).forEach((day, dayIndex) => {
			getNormalizedDayExercises(day).forEach((exercise, exerciseIndex) => {
				if (!selectedKeys.has(exerciseKey(dayIndex, exerciseIndex))) return;
				if (isSharedReadonly(exercise)) return;
				items.push({ ...exercise });
			});
		});
		if (items.length === 0) {
			toast.error(t("pbClipboardNothingToCopy"));
			return;
		}
		onCopy({ kind: "exercises", items, source: buildSource(null) });
		setSelectionMode(false);
		setSelectedKeys(new Set());
	}, [week, selectedKeys, onCopy, buildSource, t]);

	const exitSelectionMode = () => {
		setSelectionMode(false);
		setSelectedKeys(new Set());
	};

	return (
		<div className="flex flex-col h-full min-h-0">
			<div className="px-2 py-2 border-b border-border space-y-2 shrink-0">
				<div className="flex items-center gap-1.5">
					<ProgramSourcePicker
						currentProgramId={programId}
						currentProgramTitle={program?.title}
						value={sourceId}
						onChange={handleSourceChange}
						disabled={loading}
					/>
					{!isCurrentSource && onSwap && sourceProgram && (
						<HintTooltip label={t("pbSwapWithEditor")}>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								onClick={() => onSwap(sourceId, safeWeekIndex)}
								aria-label={t("pbSwapWithEditor")}
								className="h-8 w-8 shrink-0 rounded-sm text-muted-foreground hover:text-volt"
							>
								<ArrowLeftRight className="w-3.5 h-3.5" />
							</Button>
						</HintTooltip>
					)}
					{isWeekMode && isCurrentSource && (
						<HintTooltip
							label={
								followEditor
									? t("pbReferenceFollowOn")
									: t("pbReferenceFollowOff")
							}
						>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								onClick={() => setFollowEditor((prev) => !prev)}
								aria-label={
									followEditor
										? t("pbReferenceFollowOn")
										: t("pbReferenceFollowOff")
								}
								aria-pressed={followEditor}
								className={`h-8 w-8 shrink-0 rounded-sm ${
									followEditor
										? "text-volt bg-volt/10"
										: "text-muted-foreground hover:text-foreground"
								}`}
							>
								<Anchor className="w-3.5 h-3.5" />
							</Button>
						</HintTooltip>
					)}
					{isWeekMode && (
						<HintTooltip label={t("pbReferenceSelect")}>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								onClick={() =>
									selectionMode ? exitSelectionMode() : setSelectionMode(true)
								}
								aria-label={t("pbReferenceSelect")}
								aria-pressed={selectionMode}
								className={`h-8 w-8 shrink-0 rounded-sm ${
									selectionMode
										? "text-volt bg-volt/10"
										: "text-muted-foreground hover:text-foreground"
								}`}
							>
								<CheckSquare className="w-3.5 h-3.5" />
							</Button>
						</HintTooltip>
					)}
				</div>

				<div className="flex items-center gap-1 overflow-x-auto">
					{/* Week / Progression switch, kept next to the week rail it
					    replaces so the header never grows a third row. */}
					{[
						{ value: "week", icon: CalendarRange, label: t("pbViewWeek") },
						{
							value: "progression",
							icon: TrendingUp,
							label: t("pbViewProgression"),
						},
					].map((option) => {
						const OptionIcon = option.icon;
						const isActive = viewMode === option.value;
						return (
							<HintTooltip key={option.value} label={option.label}>
								<button
									type="button"
									onClick={() => {
										exitSelectionMode();
										if (option.value === "progression" && !isDesktopViewport) {
											setIsMatrixFullscreen(true);
											return;
										}
										setViewMode(option.value);
									}}
									aria-label={option.label}
									aria-pressed={
										option.value === "progression" && !isDesktopViewport
											? isMatrixFullscreen
											: isActive
									}
									className={`shrink-0 inline-flex items-center justify-center w-8 h-8 md:w-6 md:h-6 rounded-sm border transition-colors ${
										isActive
											? "border-volt bg-volt/10 text-volt"
											: "border-border text-muted-foreground hover:border-volt/50"
									}`}
								>
									<OptionIcon className="w-3 h-3" />
								</button>
							</HintTooltip>
						);
					})}

					{!isWeekMode && weeks.length > 0 && (
						<HintTooltip label={t("pbProgressionFullscreen")}>
							<button
								type="button"
								onClick={() => setIsMatrixFullscreen(true)}
								aria-label={t("pbProgressionFullscreen")}
								className="shrink-0 ml-1 inline-flex items-center justify-center w-6 h-6 rounded-sm border border-border text-muted-foreground hover:border-volt/50 hover:text-volt"
							>
								<Maximize2 className="w-3 h-3" />
							</button>
						</HintTooltip>
					)}

					{isWeekMode && weeks.length > 0 && (
						<span className="shrink-0 w-px h-4 bg-border mx-0.5" />
					)}

					{isWeekMode &&
						weeks.map((weekOption, index) => {
							const isActive = index === safeWeekIndex;
							const isEditorWeek =
								isCurrentSource && index === selectedWeekIndex;
							return (
								<button
									key={weekOption?.week_id || index}
									type="button"
									onClick={() => {
										setFollowEditor(false);
										setWeekIndex(index);
									}}
									className={`shrink-0 inline-flex items-center gap-1 px-2 h-6 rounded-sm border text-xs md:text-[11px] font-heading transition-colors ${
										isActive
											? "border-volt bg-volt/10 text-volt"
											: "border-border text-muted-foreground hover:border-volt/50"
									}`}
								>
									{`${t("weekShort")} ${index + 1}`}
									{/* A pencil, not a dot: the editor's week tabs use coloured
									    dots for publish state, and the same shape here read as
									    "this week is active for the athlete". */}
									{isEditorWeek && (
										<Pencil
											className="w-2.5 h-2.5 text-acid shrink-0"
											aria-label={t("pbReferenceEditorWeek")}
											title={t("pbReferenceEditorWeek")}
										/>
									)}
								</button>
							);
						})}
				</div>

				{isWeekMode && selectionMode && (
					<div className="flex items-center gap-2">
						<Button
							type="button"
							size="sm"
							onClick={handleCopySelection}
							disabled={selectedKeys.size === 0}
							className="h-7 rounded-sm bg-volt hover:bg-volt/90 text-xs"
						>
							<ClipboardCopy className="w-3.5 h-3.5 mr-1" />
							{`${t("pbReferenceCopySelection")} ${selectedKeys.size || ""}`.trim()}
						</Button>
						<Button
							type="button"
							variant="ghost"
							size="sm"
							onClick={exitSelectionMode}
							className="h-7 rounded-sm text-xs text-muted-foreground"
						>
							<X className="w-3.5 h-3.5 mr-1" />
							{t("cancel")}
						</Button>
					</div>
				)}
			</div>

			<div
				className={`flex-1 min-h-0 ${
					isWeekMode ? "overflow-y-auto" : "overflow-hidden"
				}`}
			>
				{loading ? (
					<div className="flex items-center justify-center py-10 text-muted-foreground">
						<Loader2 className="w-5 h-5 animate-spin" />
					</div>
				) : !sourceProgram ? (
					<p className="px-3 py-6 text-xs text-muted-foreground text-center">
						{t("pbReferencePickSource")}
					</p>
				) : weeks.length === 0 ? (
					<p className="px-3 py-6 text-xs text-muted-foreground text-center">
						{t("pbReferenceNoWeeks")}
					</p>
				) : isWeekMode ? (
					<ReferenceWeekView
						week={week}
						// The editor stays on its own week: this only scrolls it to the
						// day at the same position, which is the comparison a coach wants.
						onJumpToDay={
							onNavigate
								? (dayIndex) => onNavigate(selectedWeekIndex, dayIndex)
								: undefined
						}
						selectionMode={selectionMode}
						selectedKeys={selectedKeys}
						onToggleSelect={handleToggleSelect}
						onCopyExercise={handleCopyExercise}
						onCopyDay={handleCopyDay}
					/>
				) : (
					<ProgressionMatrix
						weeks={weeks}
						activeWeekIndex={isCurrentSource ? selectedWeekIndex : -1}
						// Cells only jump the editor when they belong to the program the
						// editor actually has open.
						onNavigate={isCurrentSource ? onNavigate : undefined}
					/>
				)}
			</div>

			{/* Programs never edited from here: the panel is a source, not a target. */}
			{sourceProgram && !isCurrentSource && (
				<p className="px-2 py-1.5 border-t border-border text-[11px] md:text-[10px] text-muted-foreground shrink-0">
					{t("pbReferenceReadOnlyHint")}
				</p>
			)}
			{/* The matrix wants ~1000px for five weeks: the dock rarely has it, so it
			    can be opened full width without leaving the builder. */}
			<Dialog open={isMatrixFullscreen} onOpenChange={setIsMatrixFullscreen}>
				<DialogContent className="bg-card border-border w-[calc(100vw-2rem)] max-w-none sm:max-w-none h-[88vh] p-0 flex flex-col gap-0 overflow-hidden">
					<DialogDescription className="hidden" />
					<DialogHeader className="px-4 pt-4 pb-3 border-b border-border shrink-0">
						<DialogTitle className="font-heading uppercase tracking-tight text-left">
							{`${t("pbViewProgression")} · ${(sourceProgram?.title || "").trim()}`}
						</DialogTitle>
					</DialogHeader>
					<div className="flex-1 min-h-0">
						{isMatrixFullscreen && (
							<ProgressionMatrix
								weeks={weeks}
								activeWeekIndex={isCurrentSource ? selectedWeekIndex : -1}
								onNavigate={
									isCurrentSource && onNavigate
										? (weekIndex, dayIndex) => {
												// Jumping the editor is pointless behind a modal.
												setIsMatrixFullscreen(false);
												onNavigate(weekIndex, dayIndex);
											}
										: undefined
								}
							/>
						)}
					</div>
				</DialogContent>
			</Dialog>

			{programId === "new" && isCurrentSource && (
				<p className="px-2 py-1.5 border-t border-border text-[11px] md:text-[10px] text-muted-foreground shrink-0">
					{t("pbReferenceUnsavedHint")}
				</p>
			)}
		</div>
	);
}
